import { pickBy } from 'lodash';
import {
    NEXT_MAINTAIN_KEY,
    CACHE_OBJECT_KEY,
    DEFAULT_EXPIRATION_PERIOD,
    MAINTENANCE_PERIOD
} from '../constants';

/**
 * A class that represents a client-side cache with TTL.
 * This is implemented by storing a json object in the browser storage, this object
 * contains the cache keys, values and TTLs. The cache cleans up stale objects once per day lazily when 'set()' is invoked.
 */
export default class CacheObject {
    constructor() {
        this.storage = window && window.localStorage;
        const cacheJson = this.getFromStorage(CACHE_OBJECT_KEY) || {};
        this.cache = cacheJson || {};
    }

    available() {
        return !!this.storage;
    }

    getFromStorage(key) {
        if (!this.available()) {
            return null;
        }

        const value = this.storage.getItem(key);

        try {
            return JSON.parse(value);
        } catch (error) {
            return value;
        }
    }

    setInStorage(key, value) {
        if (!this.available()) {
            return;
        }
        this.storage.setItem(
            key,
            JSON.stringify(value)
        );
    }

    /**
     * Sets the key in the cache with desired TTL. Then will perform cache maintenance if needed
     * @param {String} key
     * @param {Any} value
     * @param {Number} TTL
     */
    set(key, value, TTL = this.nextDays(DEFAULT_EXPIRATION_PERIOD)) {
        this.cache[key] = {
            value,
            TTL
        };

        this.maintainIfNeeded();
        this.setInStorage(CACHE_OBJECT_KEY, this.cache);
    }

    /**
     * Fetched data stored in the cache.
     * @param {String} key
     * @returns {null|*}
     */
    get(key) {
        const data = this.cache[key];
        if (!data) {
            return null;
        }

        return data.value;
    }

    /**
     * Does cache maintenance if needed.
     * The next maintenance time is saved on a spacial key.
     * Afterwards it sets the next maintenance time.
     */
    maintainIfNeeded() {
        if (!this.available()) {
            return;
        }
        const nextMaintain = this.cache[NEXT_MAINTAIN_KEY];

        if (!nextMaintain || nextMaintain < Date.now()) {
            this.maintain();
            this.cache[NEXT_MAINTAIN_KEY] = this.nextDays(MAINTENANCE_PERIOD);
        }
    }

    /**
     * Does cache maintenance by iterating over all the cache keys keeping only does with a valid TTL:
     * item.TTL >  Date.now()
     */
    maintain() {
        this.cache = pickBy(this.cache, (value, key) =>
            key === NEXT_MAINTAIN_KEY || value.TTL > Date.now()
        );
    }

    nextDays(days) {
        const next = new Date();
        return next.setDate(new Date().getDate()+days);
    }
}
